import type { Insight } from "schemas/generated/insights";

import React from "react";

import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { successToast } from "@v1/components/designSystem/alert/Toast";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { isInsightBuildableInReplo } from "@v1/features/analytics/insights/utils";
import useUrlHosts from "@v2/features/analytics/hooks/useUrlHosts";
import { generateDeepDiveNavigationPathFromUrl } from "@v2/features/analytics/utils/deepDiveNavigation";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { routes } from "@v2/utils/router";
import {
  ArrowRight,
  ChartSpline,
  Info,
  Lightbulb,
  ThumbsDown,
  ThumbsUp,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { generatePath, Link, useNavigate } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { getUrlWithoutQueryParams } from "replo-utils/lib/string";

import { useAnalyticsQueryContext } from "../contexts/AnalyticsQueryContext";

export const InsightsCard: React.FC<{
  insight: Insight;
  shouldBlurContent?: boolean;
}> = ({ insight, shouldBlurContent = false }) => {
  const navigate = useNavigate();

  const { workspaceId, projectId } = useAnalyticsQueryContext();

  const { title, analysis, action, sentiment, url, actionTitle, elementId } =
    insight;
  const isBuildableInReplo = isInsightBuildableInReplo(insight);
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = React.useState(false);

  const formattedUrl = getUrlWithoutQueryParams(url);

  const logEvent = useLogAnalytics();

  const icon = exhaustiveSwitch({ type: sentiment })({
    positive: () => <TrendingUp className="text-emerald" size={14} />,
    negative: () => <TrendingDown className="text-rose" size={14} />,
    neutral: () => <Info className="text-sky" size={14} />,
  });

  const handleThumbsClick = (thumbsType: "up" | "down") => {
    logEvent("analytics.insights.card.thumbs.click", {
      insightId: insight.id,
      thumbsType,
      workspaceId,
      projectId,
    });

    setIsFeedbackSubmitted(true);
    successToast("Feedback Submitted", "Thank you for your feedback!");
  };

  const handleApplyClick = () => {
    if (!elementId || !projectId) {
      return;
    }

    logEvent("analytics.insights.card.apply.click", {
      insightId: insight.id,
      workspaceId,
      projectId,
      elementId,
    });

    navigate(generatePath(routes.editor.element, { projectId, elementId }));
  };

  const urlHostsObj = useUrlHosts();

  const { deepDivePathWithCompressedUrlSyncedQuery } =
    generateDeepDiveNavigationPathFromUrl({
      url,
      urlHosts: urlHostsObj.data ?? [],
      path: new URL(url).pathname,
      workspaceId: workspaceId ?? "",
      projectId: projectId ?? null,
      selectedTimePeriod: {
        type: "relative",
        value: "last-week",
      },
    });

  const handleUrlPathClick = () => {
    logEvent("analytics.insights.card.url.click", {
      insightId: insight.id,
      workspaceId,
      projectId,
      url,
    });
  };

  const BLUR_CLASSNAME = "blur-xs pointer-events-none select-none";

  return (
    <div className="border-border flex-1 space-y-3 rounded-lg border p-3">
      <div className="flex flex-row gap-2">
        <div
          className={twMerge(
            "border-0.5 border-seconary flex h-6 w-6 items-center justify-center rounded-lg p-0.5",
            sentiment === "positive" && "bg-emerald-soft border-emerald-border",
            sentiment === "negative" && "bg-rose-soft border-rose-border",
            sentiment === "neutral" && "bg-sky-soft border-sky-border",
          )}
        >
          {icon}
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex flex-col gap-0.5">
            <span className="header-h6">{title}</span>
            <Link
              to={formattedUrl ?? ""}
              target="_blank"
              className={twMerge(
                "text-placeholder truncate text-xs hover:underline",
                shouldBlurContent && BLUR_CLASSNAME,
              )}
              rel="noopener noreferrer"
              onClick={handleUrlPathClick}
            >
              {formattedUrl}
            </Link>
          </div>
          <span
            className={twMerge(
              "text-muted text-xs",
              shouldBlurContent && BLUR_CLASSNAME,
            )}
          >
            {analysis}
          </span>
        </div>
        {deepDivePathWithCompressedUrlSyncedQuery && (
          <Tooltip content="View Page Analytics" triggerAsChild>
            <Link to={deepDivePathWithCompressedUrlSyncedQuery}>
              <DeprecatedIconButton
                icon={<ChartSpline className="h-4 w-4" />}
                variant="tertiary"
                size="sm"
              />
            </Link>
          </Tooltip>
        )}
      </div>
      <div
        className={twMerge(
          "bg-sheet border-0.5 border-border flex rounded-lg px-3 py-2",
          shouldBlurContent && BLUR_CLASSNAME,
        )}
      >
        <div className="flex items-center gap-1">
          <div className="flex h-full items-start">
            <Lightbulb size={16} />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-semibold">{actionTitle}</span>
            <span className="text-muted text-xs">{action}</span>
          </div>
        </div>
      </div>

      <div
        className={twMerge(
          "flex flex-row",
          shouldBlurContent && BLUR_CLASSNAME,
        )}
      >
        {!isFeedbackSubmitted && (
          <div className="flex flex-row justify-start">
            <DeprecatedIconButton
              variant="tertiary"
              size="sm"
              icon={<ThumbsUp size={12} />}
              onClick={() => handleThumbsClick("up")}
            />
            <DeprecatedIconButton
              variant="tertiary"
              size="sm"
              icon={<ThumbsDown size={12} />}
              onClick={() => handleThumbsClick("down")}
            />
          </div>
        )}

        {isBuildableInReplo && (
          <div className="flex w-full flex-row justify-end">
            <DeprecatedButton
              variant="primary"
              size="sm"
              endEnhancer={<ArrowRight size={12} />}
              onClick={handleApplyClick}
            >
              Open in Editor
            </DeprecatedButton>
          </div>
        )}
      </div>
    </div>
  );
};
